import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Card, Badge, InputGroup, Spinner, Accordion } from 'react-bootstrap';
import axios from 'axios';
import config from '../../../config';
import { toast } from 'react-toastify';
import { jwtDecode } from "jwt-decode";
import { FaBox, FaWarehouse, FaCalculator, FaBoxOpen, FaShoppingCart } from 'react-icons/fa';

const StoreFinishedProductModalEnhanced = ({ show, handleClose, batchId, batchDetails, refreshBatch }) => {
  const [locations, setLocations] = useState([]);
  const [packingMaterials, setPackingMaterials] = useState([]);
  const [formData, setFormData] = useState({
    productId: '',
    quantity: '',
    unit: '',
    packingMaterialId: '',
    quantityPerBag: '',
    numberOfBags: '',
    locationId: '',
    notes: '',
    salesOrderId: '',       // NEW: To store selected Sales Order ID
    salesOrderItemId: '',   // NEW: To store selected Sales Order Item ID
  });
  const [loading, setLoading] = useState(false);
  const [selectedPackingMaterial, setSelectedPackingMaterial] = useState(null);
  const [autoCalculating, setAutoCalculating] = useState(false);
  const [salesOrders, setSalesOrders] = useState([]); // NEW: State for fetched sales orders
  const [showSalesOrderModal, setShowSalesOrderModal] = useState(false); // NEW: State to toggle SO modal
  const [fetchingSalesOrders, setFetchingSalesOrders] = useState(false); // NEW: State for loading sales orders

  useEffect(() => {
    if (show && batchDetails?.outputs?.length > 0) {
      // Set first output product as default
      const firstOutput = batchDetails.outputs[0];
      setFormData(prev => ({
        ...prev,
        productId: firstOutput.productId._id,
        unit: firstOutput.unit,
        quantity: '',
        packingMaterialId: '',
        quantityPerBag: '',
        numberOfBags: '',
        locationId: '',
        notes: '',
        salesOrderId: '',
        salesOrderItemId: '',
      }));
      fetchLocations(firstOutput.productId._id);
      fetchPackingMaterials(firstOutput.productId._id);
      setSalesOrders([]); // Clear previous sales orders
      setShowSalesOrderModal(false); // Hide SO modal
    }
  }, [show, batchDetails]);

  const fetchLocations = async (productId) => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/new-warehouses/storage`);
      setLocations(res.data);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setLocations([]);
    }
  };

  const fetchPackingMaterials = async (productId) => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/production-batches/packing-materials/product/${productId}`);
      setPackingMaterials(res.data);
    } catch (err) {
      console.error('Error fetching packing materials:', err);
      toast.error('Failed to load packing materials');
      setPackingMaterials([]);
    }
  };

  // NEW: Function to fetch sales orders for the selected product
  const fetchSalesOrdersForProduct = async (productId) => {
    setFetchingSalesOrders(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/sales-orders/for-product/${productId}/batch/${batchId}`);
      const activeOrders = res.data.filter(
        so => !['Fully Dispatched', 'Cancelled', 'Completed'].includes(so.status)
      );
      setSalesOrders(activeOrders);
      if (activeOrders.length === 0) {
        toast.info('No active (pending dispatch) Sales Orders found for this product.');
      }
    } catch (err) {
      console.error('Error fetching sales orders:', err);
      toast.error('Failed to load sales orders.');
      setSalesOrders([]);
    } finally {
      setFetchingSalesOrders(false);
    }
  };

  const handleProductChange = (productId) => {
    const selectedOutput = batchDetails.outputs.find(output => output.productId._id === productId);
    setFormData(prev => ({
      ...prev,
      productId,
      unit: selectedOutput?.unit || '',
      quantity: '',
      packingMaterialId: '',
      quantityPerBag: '',
      numberOfBags: '',
      locationId: '',
      salesOrderId: '',
      salesOrderItemId: '',
    }));
    setSelectedPackingMaterial(null);
    fetchLocations(productId);
    fetchPackingMaterials(productId);
    setSalesOrders([]); // Clear sales orders when product changes
    setShowSalesOrderModal(false); // Hide SO modal
  };

  const handlePackingMaterialChange = (packingMaterialId) => {
    const material = packingMaterials.find(pm => pm._id === packingMaterialId);
    setSelectedPackingMaterial(material);

    setFormData(prev => ({
      ...prev,
      packingMaterialId,
      quantityPerBag: material?.quantityPerBag || ''
    }));

    // Auto-calculate bags if quantity is already entered
    if (formData.quantity && material?.quantityPerBag) {
      calculateNumberOfBags(formData.quantity, material.quantityPerBag);
    }
  };

  const calculateNumberOfBags = (totalQuantity, quantityPerBag) => {
    if (totalQuantity && quantityPerBag && quantityPerBag > 0) {
      setAutoCalculating(true);
      const bags = Math.ceil(parseFloat(totalQuantity) / parseFloat(quantityPerBag));
      setFormData(prev => ({
        ...prev,
        numberOfBags: bags
      }));

      setTimeout(() => setAutoCalculating(false), 500);
    }
  };

  const handleQuantityChange = (quantity) => {
    setFormData(prev => ({ ...prev, quantity }));

    // Auto-calculate bags when quantity changes
    if (formData.quantityPerBag && quantity) {
      calculateNumberOfBags(quantity, formData.quantityPerBag);
    }
  };

  // NEW: Handle Sales Order selection
  const handleSelectSOFromModal = (selectedSO) => {
    if (!selectedSO) return;

    let newPackingMaterialId = '';
    let newQuantityPerBag = '';
    let newPMWarehouseLocation = '';
    let newNumberOfBags = '';

    // Auto-fill packing material if available in SO item
    if (selectedSO.packingBagType) {
      const matchingPM = packingMaterials.find(pm => pm._id === selectedSO.packingBagType._id);
      if (matchingPM && matchingPM.stockByWarehouse.length > 0) {
        newPackingMaterialId = matchingPM._id;
        newQuantityPerBag = matchingPM.quantityPerBag;
        newPMWarehouseLocation = matchingPM.stockByWarehouse[0].warehouseId;
        setSelectedPackingMaterial(matchingPM);
        // toast.success(`Packing material "${matchingPM.name}" pre-selected from Sales Order.`);

        // Calculate bags
        if (selectedSO.productQuantity && newQuantityPerBag) {
          newNumberOfBags = Math.ceil(parseFloat(selectedSO.productQuantity) / parseFloat(newQuantityPerBag));
        }
      } else {
        toast.warn(`Packing material from Sales Order ("${selectedSO.packingBagType.name}") is either not configured for this product or has no available stock.`);
      }
    } else {
      toast.info('No specific packing material found in selected Sales Order item.');
    }

    setFormData(prev => ({
      ...prev,
      salesOrderId: selectedSO._id,
      salesOrderItemId: selectedSO.salesOrderItemId,
      quantity: selectedSO.productQuantity,
      packingMaterialId: newPackingMaterialId,
      quantityPerBag: newQuantityPerBag,
      PMwarehouseLocation: newPMWarehouseLocation,
      numberOfBags: newNumberOfBags,
    }));

    setShowSalesOrderModal(false); // Hide SO modal after choosing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const decoded = jwtDecode(token);
      const createdBy = decoded.userId;

      // Validate that all required fields are filled
      if (!formData.packingMaterialId) {
        toast.error('Please select a packing material');
        setLoading(false);
        return;
      }

      if (!formData.numberOfBags || formData.numberOfBags <= 0) {
        toast.error('Number of bags must be greater than 0');
        setLoading(false);
        return;
      }

      await axios.post(`${config.API_BASE_URL}/production-batches/${batchId}/store-finished-product`, {
        ...formData,
        quantity: parseFloat(formData.quantity),
        quantityPerBag: parseFloat(formData.quantityPerBag),
        numberOfBags: parseInt(formData.numberOfBags),
        PMwarehouseLocation: formData.PMwarehouseLocation, // Selected packing material warehouse
        batchId,
        createdBy
      });

      toast.success('Finished product stored successfully!');
      handleClose();
      refreshBatch();

      // Reset form
      setFormData({
        productId: '',
        quantity: '',
        unit: '',
        packingMaterialId: '',
        quantityPerBag: '',
        numberOfBags: '',
        locationId: '',
        notes: '',
        salesOrderId: '',
        salesOrderItemId: '',
      });
      setSelectedPackingMaterial(null);
      setSalesOrders([]);
      setShowSalesOrderModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to store finished product');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getMaxQuantity = () => {
    // Calculate total calculated output for the entire batch
    const totalCalculatedBatchOutput = batchDetails?.outputs.reduce((sum, output) => sum + (output.calculatedOutput || 0), 0) || 0;

    // Calculate total stored quantity for the entire batch (sum of storedQuantity for all outputs)
    const totalStoredBatchQuantity = batchDetails?.outputs.reduce((sum, output) => sum + (output.storedQuantity || 0), 0) || 0;

    // Calculate remaining quantity for the entire batch
    const remainingBatchQuantity = totalCalculatedBatchOutput - totalStoredBatchQuantity;

    // Ensure the remaining quantity is not negative
    return Math.max(0, remainingBatchQuantity);
  };

  const getRemainingQuantity = () => {
    const selectedOutput = batchDetails?.outputs?.find(output => output.productId._id === formData.productId);

    const totalCalculatedBatchOutput = batchDetails?.outputs.reduce((sum, output) => sum + (output.calculatedOutput || 0), 0) || 0;
    const totalStoredBatchQuantity = batchDetails?.outputs.reduce((sum, output) => sum + (output.storedQuantity || 0), 0) || 0;

    return {
      calculatedIndividual: selectedOutput?.calculatedOutput || 0,
      storedIndividual: selectedOutput?.storedQuantity || 0,
      calculatedBatch: totalCalculatedBatchOutput,
      storedBatch: totalStoredBatchQuantity,
      remainingBatch: getMaxQuantity()
    };
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <FaWarehouse className="me-2" />
            Store Finished Product to Warehouse
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {batchDetails?.outputs?.length === 0 ? (
              <Alert variant="warning">
                <FaBoxOpen className="me-2" />
                No output products defined in BOM for this batch.
              </Alert>
            ) : (
              <>
                {/* Batch Info Card */}
                <Card className="mb-3 border-0 bg-light">
                  <Card.Body className="py-2">
                    <Row>
                      <Col>
                        <small className="text-muted">Batch Number:</small>
                        <div className="fw-bold">{batchDetails.batchNumber}</div>
                      </Col>
                      <Col>
                        <small className="text-muted">Status:</small>
                        <div>
                          <Badge bg={batchDetails.status === 'in-progress' ? 'primary' : 'success'}>
                            {batchDetails.status}
                          </Badge>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Product Selection */}
                <Row className="mb-3">
                  <Col>
                    <Form.Label className="fw-bold">
                      <FaBox className="me-2" />
                      Output Product *
                    </Form.Label>
                    <InputGroup>
                      <Form.Select
                        value={formData.productId}
                        onChange={(e) => handleProductChange(e.target.value)}
                        required
                        className="form-select-lg"
                      >
                        <option value="">-- Select Output Product --</option>
                        {batchDetails.outputs.map((output) => {
                          const remaining = (output.calculatedOutput || 0) - (output.storedQuantity || 0);
                          return (
                            <option
                              key={output.productId._id}
                              value={output.productId._id}
                              // disabled={remaining <= 0}
                            >
                              {output.productId.productName}
                              (Available: {remaining.toFixed(2)} {output.unit})
                            </option>
                          );
                        })}
                      </Form.Select>
                      {formData.productId && (
                        <Button
                          variant="outline-secondary"
                          onClick={() => {
                            setShowSalesOrderModal(true);
                            fetchSalesOrdersForProduct(formData.productId);
                          }}
                          disabled={!formData.productId || fetchingSalesOrders}
                        >
                          {fetchingSalesOrders ? 'Loading SOs...' : "Show Related SO's"}
                        </Button>
                      )}
                    </InputGroup>

                    {formData.productId && (
                      <div className="mt-2">
                        <small className="text-muted">
                          Total Calculated (Batch): <strong>{getRemainingQuantity().calculatedBatch.toFixed(2)} {formData.unit}</strong> |
                          Already Stored (Batch): <strong>{getRemainingQuantity().storedBatch.toFixed(2)} {formData.unit}</strong> |
                          Remaining (Batch): <strong className="text-success">{getRemainingQuantity().remainingBatch.toFixed(2)} {formData.unit}</strong>
                        </small>
                        {formData.productId && (formData.quantity > getRemainingQuantity().calculatedIndividual - getRemainingQuantity().storedIndividual) && (
                          <Alert variant="info" className="mt-2 mb-0 py-1">
                            <small>
                              Note: You are entering more than the individual product's calculated output.
                              This is allowed as long as the total stored quantity for the batch (across all products)
                              does not exceed the total calculated output for the batch.
                            </small>
                          </Alert>
                        )}
                      </div>
                    )}
                  </Col>
                </Row>

                {/* Quantity Input */}
                <Row className="mb-3">
                  <Col md={8}>
                    <Form.Label className="fw-bold">Quantity to Store *</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        max={getMaxQuantity()}
                        value={formData.quantity}
                        onChange={(e) => handleQuantityChange(e.target.value)}
                        placeholder={`Max: ${getMaxQuantity().toFixed(2)}`}
                        required
                        disabled={!formData.productId}
                      />
                      <InputGroup.Text className="bg-light">
                        {formData.unit || 'unit'}
                      </InputGroup.Text>
                    </InputGroup>
                  </Col>
                  <Col md={4} className="d-none">
                    <Form.Label className="fw-bold">Unit</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.unit}
                      readOnly
                      className="bg-light"
                    />
                  </Col>
                </Row>

                {/* Packing Material Selection */}
                <Row className="mb-3">
                  <Col>
                    <Form.Label className="fw-bold">
                      <FaBoxOpen className="me-2" />
                      Packing Material *
                    </Form.Label>
                    <Form.Select
                      value={formData.packingMaterialId + '|' + (formData.PMwarehouseLocation || '')}
                      onChange={(e) => {
                        const [materialId, warehouseId] = e.target.value.split('|');
                        handlePackingMaterialChange(materialId);
                        setFormData(prev => ({ ...prev, PMwarehouseLocation: warehouseId }));
                      }}
                      required
                      disabled={!formData.productId || !formData.quantity}
                      className="form-select-lg"
                    >
                      <option value="">-- Select Packing Material (with available stock) --</option>
                      {packingMaterials.map((material) =>
                        material.stockByWarehouse.map((warehouse, index) => (
                          <option
                            key={`${material._id}-${warehouse.warehouseId}-${index}`}
                            value={`${material._id}|${warehouse.warehouseId}`}
                          >
                            {material.name} ({material.alias}) - {warehouse.availableQuantity} {warehouse.unit} available at {warehouse.warehouseName} - Capacity: {material.quantityPerBag} {material.unit} per bag
                          </option>
                        ))
                      )}
                    </Form.Select>

                    {packingMaterials.length === 0 && formData.productId && (
                      <Alert variant="warning" className="mt-2 mb-0 py-2">
                        <small>
                          No packing materials with available stock found for this product.
                          Please ensure packing materials are configured and have stock available.
                        </small>
                      </Alert>
                    )}
                  </Col>
                </Row>

                {/* Packing Material Info Card */}
                {selectedPackingMaterial && (
                  <Card className="mb-3 border-primary">
                    <Card.Body className="py-2">
                      <Row>
                        <Col md={4}>
                          <small className="text-muted">Material:</small>
                          <div className="fw-bold">{selectedPackingMaterial.name}</div>
                        </Col>
                        <Col md={4}>
                          <small className="text-muted">Size:</small>
                          <div>{selectedPackingMaterial.size}</div>
                        </Col>
                        <Col md={4}>
                          <small className="text-muted">Capacity per Bag:</small>
                          <div className="fw-bold text-primary">
                            {selectedPackingMaterial.quantityPerBag} {selectedPackingMaterial.unit}
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}

                {/* Auto-calculated Number of Bags */}
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Label className="fw-bold">Quantity per Bag</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={formData.quantityPerBag}
                        readOnly
                        className="bg-light"
                      />
                      <InputGroup.Text className="bg-light">
                        {formData.unit || 'unit'}
                      </InputGroup.Text>
                    </InputGroup>
                  </Col>
                  <Col md={6}>
                    <Form.Label className="fw-bold">
                      <FaCalculator className="me-2" />
                      Number of Bags *
                      {autoCalculating && (
                        <Badge bg="success" className="ms-2 animate-pulse">
                          Auto-calculated
                        </Badge>
                      )}
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-primary text-white">
                        <FaBox />
                      </InputGroup.Text>
                      <Form.Control
                        type="number"
                        min="1"
                        value={formData.numberOfBags}
                        onChange={(e) => setFormData(prev => ({ ...prev, numberOfBags: e.target.value }))}
                        className="form-control fw-bold"
                        required
                        disabled={!formData.quantityPerBag}
                      />
                      <InputGroup.Text className="bg-light">
                        bags
                      </InputGroup.Text>
                    </InputGroup>
                    {formData.quantity && formData.quantityPerBag && formData.numberOfBags && (
                      <small className="text-muted">
                        Total capacity: {(formData.quantityPerBag * formData.numberOfBags).toFixed(2)} {formData.unit}
                        {(formData.quantityPerBag * formData.numberOfBags) < formData.quantity && (
                          <span className="text-danger ms-1">⚠️ Insufficient capacity!</span>
                        )}
                      </small>
                    )}
                  </Col>
                </Row>

                {/* Warehouse Location */}
                <Row className="mb-3">
                  <Col>
                    <Form.Label className="fw-bold">
                      <FaWarehouse className="me-2" />
                      Warehouse Location *
                    </Form.Label>
                    <Form.Select
                      value={formData.locationId}
                      onChange={(e) => setFormData(prev => ({ ...prev, locationId: e.target.value }))}
                      required
                      disabled={!formData.packingMaterialId}
                    >
                      <option value="">-- Select Warehouse Location --</option>
                      {locations.map((loc) => (
                        <option key={loc._id} value={loc._id}>
                          {loc.fullPath || loc.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>

                {/* Notes */}
                <Row className="mb-3">
                  <Col>
                    <Form.Label>Notes (Optional)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Add any additional notes about this storage..."
                    />
                  </Col>
                </Row>

                {/* Summary Card */}
                {formData.quantity && formData.packingMaterialId && formData.numberOfBags && (
                  <Card className="border-success">
                    <Card.Header className="bg-success text-white py-2">
                      <strong>Storage Summary</strong>
                    </Card.Header>
                    <Card.Body className="py-2">
                      <Row>
                        <Col md={3}>
                          <small className="text-muted">Product:</small>
                          <div className="fw-bold">
                            {batchDetails.outputs.find(o => o.productId._id === formData.productId)?.productId.productName}
                          </div>
                        </Col>
                        <Col md={3}>
                          <small className="text-muted">Quantity:</small>
                          <div className="fw-bold">{parseFloat(formData.quantity).toFixed(2)} {formData.unit}</div>
                        </Col>
                        <Col md={3}>
                          <small className="text-muted">Packing:</small>
                          <div className="fw-bold">{selectedPackingMaterial?.name}</div>
                        </Col>
                        <Col md={3}>
                          <small className="text-muted">Total Bags:</small>
                          <div className="fw-bold text-success">{formData.numberOfBags} bags</div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}
              </>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={loading || !formData.productId || !formData.quantity || !formData.packingMaterialId || !formData.locationId}
            >
              {loading ? 'Storing...' : 'Store Product'}
            </Button>
          </Modal.Footer>
        </Form>

        <style jsx>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          .animate-pulse {
            animation: pulse 1s ease-in-out infinite;
          }
        `}</style>
      </Modal>

      {/* NEW: Sales Order Selection Modal */}
      <Modal show={showSalesOrderModal} onHide={() => setShowSalesOrderModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title><FaShoppingCart className="me-2" />Related Sales Orders</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {fetchingSalesOrders ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading Sales Orders...</p>
            </div>
          ) : salesOrders.length > 0 ? (
            <Accordion defaultActiveKey="0">
              {salesOrders.map((so, index) => (
                <Accordion.Item eventKey={index.toString()} key={so._id}>
                  <Accordion.Header>
                    <div className="d-flex justify-content-between align-items-center w-100 pe-3">
                      <div className="d-flex flex-column">
                        <span>
                          {so.isLinkedToCurrentBatch ? '🔗 ' : ''}
                          <strong>{so.orderNumber}</strong> 
                          {/* - {so.buyer?.business_name || 'N/A'} */}
                        </span>
                        <small className="text-muted">Due: {so.expectedDeliveryDate ? new Date(so.expectedDeliveryDate).toLocaleDateString() : 'N/A'}</small>
                      </div>
                      <div className="d-flex flex-column align-items-center">
                        <small className="text-muted">Still Required</small>
                        <Badge bg="primary" pill>{so.productQuantity - so.dispatchedQuantity} {so.productUnit}</Badge>
                      </div>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row>
                      <Col md={6}>
                        <p className="mb-1"><strong>Ordered Quantity:</strong> {so.productQuantity} {so.productUnit}</p>
                        <p className="mb-1"><strong>Packing Material:</strong> {so.packingBagType?.name || 'Not specified'} ({so.packingBagType?.alias || ''})</p>
                        <p className="mb-1"><strong>Weight Type:</strong> {so?.weightType || 'Not specified'}</p>
                        {/* <p className="mb-1"><strong>Status:</strong> <Badge bg="info">{so.status}</Badge></p> */}
                      </Col>
                      <Col md={6}>
                        <p className="mb-1"><strong>Dispatched Quantity:</strong> {so.dispatchedQuantity} {so.productUnit}</p>
                        <p className="mb-1"><strong>Quality:</strong> {so?.quality || 'Not specified'}</p>
                        <p className="mb-1"><strong>Lab Test Required:</strong> {so.labTestRequired ? 'Yes' : 'No'}</p>
                      </Col>
                      <Col md={9}>
                        <p className="mb-1"><strong>Custom Marking:</strong> {so?.customMarking || 'Not specified'}</p>
                      </Col>
                      <Col md={3}>
                        <div className="text-end">
                          <Button variant="success" onClick={() => handleSelectSOFromModal(so)}>
                            Ok
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          ) : (
            <Alert variant="info">No relevant Sales Orders found for this product.</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSalesOrderModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default StoreFinishedProductModalEnhanced;
